import { MatDialogRef } from '@angular/material/dialog';
import { SafeUrl } from '@angular/platform-browser';
import { createAction, props } from '@ngrx/store';
import {
  ChatMessage,
  CustomerServiceNote,
  CustomerServiceNoteDetail,
  Driver,
  DriverEta,
  DriverFareCalculation,
  FareAdjustment,
  FareReviewDetail,
  FareReviewStatus,
  Itinerary,
  LyftDispatchRide,
  LyftDispatchRideFareAdjustmentReason,
  LyftDispatchRideFareReviewStatusUpdate,
  LyftDispatchRideReceipt,
  OrganizationRider,
  OrganizationRiderServiceLevel,
  OrganizationServiceLevel,
  PaymentAccount,
  PaymentMethod,
  ReservationDetail,
  ReservationRouteSummary,
  RideRequest,
  RideRequestDetail,
  RideRequestInfo,
  RiderFare,
  TransitProgramInfo,
  Trip,
  Vehicle
} from '@uzurv/clara';
import { ApplicationException, HttpException } from '@uzurv/core';
import { RiderFareCollectionDialogComponent } from '../../components/dialogs/rider-fare-collection-dialog/rider-fare-collection-dialog.component';
import { ReservationListItemVm } from '../../components/reservation';
import { EstimateActualFareCalulation } from '../../components/trip-fare-adjustment-detail/trip-fare-adjustment-detail.component';
import { TripDetailState } from './trip-detail.reducer';

export const GetEligibleVehiclesForReservation = createAction('[Trip Detail] Get eligible vehicles for reservation');

export const GetEligibleVehiclesForReservationSuccess = createAction(
  '[Trip Detail] Get eligible vehicles for reservation success',
  props<{ eligibleVehicles: Array<Vehicle> }>()
);

export const GetEligibleVehiclesForReservationFail = createAction(
  '[Trip Detail] Get eligible vehicles for reservation fail',
  props<{ error: HttpException }>()
);

export const ChangeDriverVehicle = createAction(
  '[Trip Detail] Change driver vehicle',
  props<{ trip: ReservationListItemVm; payload: Vehicle }>()
);

export const ChangeDriverVehicleSuccess = createAction('[Trip Detail] Change driver vehicle success', props<{ payload: Vehicle }>());

export const ChangeDriverVehicleFail = createAction(
  '[Trip Detail] Change driver vehicle fail',
  props<{ error: HttpException | ApplicationException }>()
);

export const RestoreState = createAction('[Trip Detail] Restore state', props<{ payload: TripDetailState }>());

export const OpenTripDetailTab = createAction(
  '[Trip Detail] Open trip detail modal',
  props<{ payload: { trip: Trip; tabSelected?: number } }>()
);

export const GetTripDetailFromUrl = createAction('[Trip Detail] Get trip detail from url', props<{ payload: string }>());

export const GetTripDetail = createAction('[Trip Detail] Get Trip detail', props<{ payload: { id: number; type: 'rr' | 'res' } }>());

export const RefreshTripDetail = createAction('[Trip Detail] Refresh Trip detail');

export const GetTripDetailSuccess = createAction(
  '[Trip Detail] Get trip detail success',
  props<{ payload: ReservationDetail | RideRequestDetail }>()
);

export const GetTripDetailFail = createAction(
  '[Trip Detail] Get trip detail fail',
  props<{ error: HttpException | ApplicationException }>()
);

export const SetPermissions = createAction(
  '[Trip Detail] Set permissions',
  props<{ payload: { canEditBasicTrip: boolean; canEditWavTrip: boolean } }>()
);

export const SwitchToActiveReservation = createAction('[Trip Detail] Switch to active reservation', props<{ payload: number }>());

export const GetRiderPaymentAccountSuccess = createAction(
  '[Trip Detail] Get rider payment account success',
  props<{ payload: PaymentAccount }>()
);

export const GetRiderPaymentAccountFail = createAction('[Trip Detail] Get rider payment account fail', props<{ error: HttpException }>());

export const GetDriverProfilePictureSuccess = createAction(
  '[Trip Detail] Get driver profile picture success',
  props<{ safeUrl: SafeUrl }>()
);

export const GetDriverProfilePictureFail = createAction('[Trip Detail] Get driver profile picture fail', props<{ error: HttpException }>());

export const GetRiderIgnoredDriversSuccess = createAction(
  '[Trip Detail] Get rider ignored drivers success',
  props<{ drivers: Array<Driver> }>()
);

export const GetRiderIgnoredDriversFail = createAction('[Trip Detail] Get rider ignored drivers fail', props<{ error: HttpException }>());

export const GetCompletedRouteSuccess = createAction(
  '[Trip Detail] Get completed route success',
  props<{ reservationRouteSummary: ReservationRouteSummary }>()
);

export const GetCompletedRouteFail = createAction(
  '[Trip Detail] Get completed route fail',
  props<{ reservationRouteSummary: ReservationRouteSummary }>()
);

export const GetItinerarySuccess = createAction('[Trip Detail] Get itinerary success', props<{ itinerary: Itinerary }>());

export const GetItineraryFail = createAction('[Trip Detail] Get itinerary fail', props<{ error: HttpException }>());

export const GoToItineraryRide = createAction('[Trip Detail] Go to itinearary ride', props<{ id: number }>());

export const FavoriteDriver = createAction('[Trip Detail] Favorite driver');

export const FavoriteDriverSuccess = createAction('[Trip Detail] Favorite driver success');

export const FavoriteDriverFail = createAction('[Trip Detail] Favorite driver fail', props<{ error: HttpException }>());

export const RemoveFavoritedDriver = createAction('[Trip Detail] Remove favorited driver');

export const RemoveFavoritedDriverSuccess = createAction('[Trip Detail] Remove favorited driver success');

export const RemoveFavoritedDriverFail = createAction('[Trip Detail] Remove favorited driver fail', props<{ error: HttpException }>());

export const IgnoreDriver = createAction('[Trip Detail] Ignore driver');

export const IgnoreDriverSuccess = createAction('[Trip Detail] Ignore driver success');

export const IgnoreDriverFail = createAction('[Trip Detail] Ignore driver fail', props<{ error: HttpException }>());

export const RemoveIgnoredDriver = createAction('[Trip Detail] Remove ignored driver');

export const RemoveIgnoredDriverSuccess = createAction('[Trip Detail] Remove ignored driver success');

export const RemoveIgnoredDriverFail = createAction('[Trip Detail] Remove ignored driver fail', props<{ error: HttpException }>());

export const SelectDriver = createAction('[Trip Detail] Select driver');

export const SelectProvider = createAction('[Trip Detail] Select provider');

export const RefreshChatMessages = createAction('[Trip Detail] Refresh chat messages');

export const ReadChatMessages = createAction('[Trip Detail] Read chat messages');

export const GetChatMessagesSuccess = createAction('[Trip Detail] Get chat messages success', props<{ payload: Array<ChatMessage> }>());

export const GetChatMessagesFail = createAction(
  '[Trip Detail] Get chat messages fail',
  props<{ error: HttpException | ApplicationException }>()
);

export const SendChatMessage = createAction('[Trip Detail] Send chat message', props<{ payload: string }>());

export const SendChatMessageSuccess = createAction('[Trip Detail] Send chat message success', props<{ chatMessage: ChatMessage }>());

export const SendChatMessageFail = createAction('[Trip Detail] Send chat message fail', props<{ error: HttpException }>());

export const GetNotesSuccess = createAction(
  '[Trip Detail] Get notes success',
  props<{ customerServiceNoteDetails: Array<CustomerServiceNoteDetail> }>()
);

export const GetNotesFail = createAction('[Trip Detail] Get notes fail', props<{ error: HttpException | ApplicationException }>());

export const AddNote = createAction('[Trip Detail] Add note', props<{ customerServiceNotes: CustomerServiceNote }>());

export const AddNoteSuccess = createAction('[Trip Detail] Add note success', props<{ customerServiceNotes: CustomerServiceNote }>());

export const AddNoteFail = createAction('[Trip Detail] Add note fail', props<{ error: HttpException }>());

export const GoToRider = createAction('[Trip Detail] Go to rider', props<{ organizationRider: OrganizationRider }>());

export const GoToDriver = createAction('[Trip Detail] Go to driver', props<{ driver: Driver }>());

export const DispatchToLyft = createAction('[Trip Detail] Dispatch to lyft');

export const DispatchToLyftSuccess = createAction('[Trip Detail] Dispatch to lyft success');

export const DispatchToLyftFail = createAction('[Trip Detail] Dispatch to lyft fail', props<{ error: HttpException }>());

export const GoToLyftDetail = createAction('[Trip Detail] Go to lyft detail');

export const GoToRecoveryRide = createAction('[Trip Detail] Go to recovery ride');

export const GoToOriginalRide = createAction('[Trip Detail] Go to original ride');

export const GoToFareAdjustment = createAction('[Trip Detail] Go to fare adjustment');

export const CancelTrip = createAction('[Trip Detail] Cancel trip');

export const CancelTripSuccess = createAction('[Trip Detail] Cancel trip success', props<{ recoveryItinerary?: Itinerary }>());

export const CancelTripFail = createAction('[Trip Detail] Cancel trip fail', props<{ error: HttpException | ApplicationException }>());

export const CreateRecovery = createAction('[Trip Detail] Create recovery');

export const CreateRecoverySuccess = createAction('[Trip Detail] Create recovery success');

export const CreateRecoveryFail = createAction(
  '[Trip Detail] Create recovery fail',
  props<{ error: HttpException | ApplicationException }>()
);

export const OpenRecoveryRideTab = createAction('[Trip Detail] Open recovery ride tab', props<{ recoveryItinerary: Itinerary }>());

export const EnterEditMode = createAction('[Trip Detail] Enter edit mode');

export const CancelEditMode = createAction('[Trip Detail] Cancel edit mode');

export const UpdateTripValue = createAction(
  '[Trip Detail] Update trip value',
  props<{ payload: { trip: ReservationListItemVm; getRideRequestInfo: boolean } }>()
);

export const UpdateRideInfoSuccess = createAction(
  '[Trip Detail] Update ride info success',
  props<{ payload: { transitProgramInfo: TransitProgramInfo; rideRequestInfo: RideRequestInfo } }>()
);

export const UpdateRideInfoFail = createAction('[Trip Detail] Update ride info fail', props<{ error: HttpException }>());

export const UpdateReservationStatus = createAction(
  '[Trip Detail] Update reservation status',
  props<{ payload: { reservationId: number; statusName: string; timestamp: Date } }>()
);

export const UpdateReservationStatusSuccess = createAction(
  '[Trip Detail] Update reservation status success',
  props<{ reservationListItemVm: ReservationListItemVm }>()
);

export const UpdateReservationStatusFail = createAction('[Trip Detail] Update reservation status fail', props<{ error: HttpException }>());

export const SaveTrip = createAction('[Trip Detail] Save trip');

export const PatchIncentive = createAction('[Trip Detail] Patch incentive', props<{ payload: number }>());

export const PatchIncentiveSuccess = createAction('[Trip Detail] Patch incentive success', props<{ rideRequest: RideRequest }>());

export const PatchIncentiveFail = createAction(
  '[Trip Detail] Patch incentive fail',
  props<{ error: HttpException | ApplicationException }>()
);

export const PatchRideRequest = createAction('[Trip Detail] Patch ride request', props<{ payload: any }>());

export const PatchRideRequestSuccess = createAction('[Trip Detail] Patch ride request success', props<{ rideRequest: RideRequest }>());

export const PatchRideRequestFail = createAction(
  '[Trip Detail] Patch ride request fail',
  props<{ error: HttpException | ApplicationException }>()
);

export const GetFareReviewStatus = createAction('[Trip Detail] Get fare review status', props<{ reservationId?: number }>());

export const GetFareReviewStatusSuccess = createAction(
  '[Trip Detail] Get fare review status success',
  props<{ fareReviewStatus: FareReviewStatus }>()
);

export const GetFareReviewStatusFail = createAction(
  '[Trip Detail] Get fare review status fail',
  props<{ error: HttpException | ApplicationException }>()
);

export const GetFareReviewDetail = createAction('[Trip Detail] Get fare review detail');

export const GetFareReviewDetailSuccess = createAction(
  '[Trip Detail] Get fare review detail success',
  props<{ fareReviewDetail: FareReviewDetail }>()
);

export const GetFareReviewDetailFail = createAction(
  '[Trip Detail] Get fare review detail fail',
  props<{ error: HttpException | ApplicationException }>()
);

export const FindActiveByReservationId = createAction('[Trip Detail] Find active by reservation id');

export const FindActiveByReservationIdSuccess = createAction(
  '[Trip Detail] Find active by reservation id success',
  props<{ lyftDispatchRide: LyftDispatchRide }>()
);

export const GetLyftDispatchRideFareAdjustmentReasons = createAction('[Trip Detail] Get lyft dispatch ride fare adjustment reasons');

export const GetLyftDispatchRideFareAdjustmentReasonsSuccess = createAction(
  '[Trip Detail] Get lyft dispatch ride fare adjustment reasons success',
  props<{ lyftDispatchRideFareAdjustmentReasons: Array<LyftDispatchRideFareAdjustmentReason> }>()
);

export const GetLyftDispatchRideReceipt = createAction('[Trip Detail] Get lyft dispatch ride receipt', props<{ lyftRideId: number }>());

export const GetLyftDispatchRideReceiptSuccess = createAction(
  '[Trip Detail] Get lyft dispatch ride receipt success',
  props<{ lyftDispatchRideReceipt: LyftDispatchRideReceipt }>()
);

export const OpenDriverFareCalculationHistoryDialog = createAction('[Trip Detail] Open driver fare calculation history dialog');

export const OpenFareAdjustmentNotesDialog = createAction('[Trip Detail] Open fare adjustment notes dialog');

export const OpenFareAndRatesDetailsDialog = createAction(
  '[Trip Detail] Open fare and rates details dialog',
  props<{ estimateActualFareCalulation: EstimateActualFareCalulation }>()
);

export const ShowFareReviewEditMode = createAction('[Trip Detail] Show fare review edit mode');

export const ShowFareReviewEditModeSuccess = createAction(
  '[Trip Detail] Show fare review edit mode success',
  props<{ reservationId?: number }>()
);

export const ShowFareReviewEditModeFail = createAction(
  '[Trip Detail] Show fare review edit mode fail',
  props<{ error: HttpException | ApplicationException }>()
);

export const GetFareCalculationNotes = createAction('[Trip Detail] Get fare calculation notes');

export const GetFareCalculationNotesSuccess = createAction(
  '[Trip Detail] Get fare calculation notes success',
  props<{ fareCalculationNotes: Array<CustomerServiceNote> }>()
);

export const GetFareAdjustmentEstimate = createAction(
  '[Trip Detail] Get fare adjustment estimate',
  props<{ fareAdjustment: FareAdjustment }>()
);

export const GetFareAdjustmentEstimateSuccess = createAction(
  '[Trip Detail] Get fare adjustment estimate success',
  props<{ estimateDriverFareCalculation: DriverFareCalculation }>()
);

export const CancelFareAdjustment = createAction('[Trip Detail] Cancel fared adjustment');

export const CancelFareAdjustmentSuccess = createAction(
  '[Trip Detail] Cancel fare adjustment success',
  props<{ reservationId?: number }>()
);

export const UpdateFareReviewStatusRequired = createAction('[Trip Detail] Update fare review status required');

export const UpdateFareReviewStatusRequiredSuccess = createAction(
  '[Trip Detail] Update fare review status required success',
  props<{ reservationId?: number }>()
);

export const SubmitFareAdjustment = createAction('[Trip Detail] Submit fare adjustment', props<{ fareAdjustment: FareAdjustment }>());

export const SubmitFareAdjustmentSuccess = createAction(
  '[Trip Detail] Submit fare adjustment success',
  props<{ reservationId?: number }>()
);

export const SubmitFareAdjustmentFail = createAction(
  '[Trip Detail] Submit fare adjustment fail',
  props<{ error: HttpException | ApplicationException }>()
);

export const ShowLyftReviewEditMode = createAction('[Trip Detail] Show lyft review edit mode');

export const ShowLyftReviewEditModeSuccess = createAction(
  '[Trip Detail] Show lyft review edit mode success',
  props<{ reservationId?: number }>()
);

export const CancelLyftReview = createAction('[Trip Detail] Cancel lyft review');

export const CancelLyftReviewSuccess = createAction('[Trip Detail] Cancel lyft review success', props<{ reservationId?: number }>());

export const SubmitLyftReview = createAction(
  '[Trip Detail] Submit lyft review',
  props<{ lyftDispatchRideFareReviewStatusUpdate: LyftDispatchRideFareReviewStatusUpdate }>()
);

export const SubmitLyftReviewSuccess = createAction('[Trip Detail] Submit lyft review success', props<{ reservationId?: number }>());

export const SubmitLyftReviewFail = createAction(
  '[Trip Detail] Submit lyft review fail',
  props<{ error: HttpException | ApplicationException }>()
);

export const OpenTripDetailTabByReservationId = createAction(
  '[Trip Detail] Open trip detail tab by reservation id',
  props<{ payload: { reservationId: number; tabselected: number } }>()
);

export const GetFareReviewNotes = createAction('[Trip Detail] Get fare review notes');

export const GetFareReviewNotesSuccess = createAction(
  '[Trip Detail] Get fare review notes success',
  props<{ fareReviewNotes: Array<CustomerServiceNoteDetail> }>()
);

export const GetFareReviewNotesFail = createAction(
  '[Trip Detail] Get fare review notes fail',
  props<{ error: HttpException | ApplicationException }>()
);

export const AddFareReviewNote = createAction('[Trip Detail] Add fare review note', props<{ fareReviewNoteText: string }>());

export const AddFareReviewNoteSuccess = createAction(
  '[Trip Detail] Add fare review note success',
  props<{ fareReviewNote: CustomerServiceNote }>()
);

export const AddFareReviewNoteFail = createAction(
  '[Trip Detail] Add fare review note fail',
  props<{ error: HttpException | ApplicationException }>()
);

export const GetOrganizationServiceLevelsSuccess = createAction(
  '[Trip Detail] Get organization service levels success',
  props<{ organizationServiceLevels: Array<OrganizationServiceLevel> }>()
);

export const GetOrganizationServiceLevelsFail = createAction(
  '[Trip Detail] Get organization service levels fail',
  props<{ error: HttpException | ApplicationException }>()
);

export const GetOrganizationRiderServiceLevelsSuccess = createAction(
  '[Trip Detail] Get organization rider service levels success',
  props<{ organizationServiceLevels: Array<OrganizationRiderServiceLevel> }>()
);

export const GetOrganizationRiderServiceLevelsFail = createAction(
  '[Trip Detail] Get organization rider service levels fail',
  props<{ error: HttpException | ApplicationException }>()
);

export const EtaReceived = createAction('[Trip Detail] ETA received', props<{ payload: DriverEta }>());

export const GetPaymentMethodsSuccess = createAction(
  '[Trip Detail] Get payment methods success',
  props<{ paymentMethods: Array<PaymentMethod> }>()
);

export const GetPaymentMethodsFail = createAction(
  '[Trip Detail] Get payment methods fail',
  props<{ error: HttpException | ApplicationException }>()
);

export const GetRiderFaresSuccess = createAction('[Trip Detail] Get rider fares success', props<{ rideFares: Array<RiderFare> }>());

export const GetRiderFaresFail = createAction(
  '[Trip Detail] Get rider fares fail',
  props<{ error: HttpException | ApplicationException }>()
);

export const EditRiderFare = createAction(
  '[Trip Detail] Open rider fare dialog',
  props<{ reservationListItemVm: ReservationListItemVm }>()
);

export const SubmitRiderFare = createAction(
  '[Trip Detail] Submit rider fare',
  props<{
    payload: {
      reservationId: number;
      riderFareArray: Array<RiderFare>;
      dialogRef: MatDialogRef<RiderFareCollectionDialogComponent>;
      reservationListItemVm: ReservationListItemVm;
    };
  }>()
);

export const SubmitRiderFareFail = createAction(
  '[Trip Detail] Get submit rider fare fail',
  props<{ error: HttpException | ApplicationException }>()
);

export const SubmitRiderFareSuccess = createAction(
  '[Trip Detail] Get submit rider fare success',
  props<{ payload: { reservationListItemVm: ReservationListItemVm; riderFares: Array<RiderFare> } }>()
);
